import React, { useMemo } from "react";
import { Grid, Paper, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { ResizableTable } from "../../../components/resizableTable/ResizableTable";
import * as d3 from "d3";

const QUARTER_BG_COLORS = ["#4471c4", "#5b9bd5"];
const d3ThousandFormatter = d3.format(",.0f");
const d3CountFormatter = d3.format(",");

const toTitleCase = (str) =>
  (str || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const buildColumns = (quarters, UICols, type, label, timeline, acvLabel) => {
  if (!quarters || !UICols || quarters.length === 0 || UICols.length === 0) return [];
  const cols = [
    {
      label: toTitleCase(timeline),
      subColumns: [{ label: label || type, field: type, type: "text", align: "left", width: 150, sortable: true, filterable: true }],
      headerBgColor: "#ffffff",
      headerTextColor: "#000000",
    },
  ];
  quarters.forEach((q, i) => {
    cols.push({
      label: q,
      subColumns: UICols.map((uiCol) => ({
        label: uiCol.type === "currency" ? acvLabel : uiCol.label,
        field: `${uiCol.col}_${q}`,
        type: "number",
        align: "right",
        width: 70,
        sortable: true,
        filterable: true,
        headerBgColor: "#ffffff",
        headerTextColor: "#333333",
      })),
      headerBgColor: QUARTER_BG_COLORS[i % 2],
      headerTextColor: "#ffffff",
    });
  });
  if (quarters.length > 1) {
    cols.push({
      label: "Total",
      subColumns: UICols.map((uiCol) => ({
        label: uiCol.type === "currency" ? acvLabel : uiCol.label,
        field: `${uiCol.col}_Total`,
        type: "number",
        align: "right",
        width: 70,
        sortable: true,
        filterable: true,
        headerBgColor: "#ffffff",
        headerTextColor: "#333333",
      })),
      headerBgColor: QUARTER_BG_COLORS[quarters.length % 2],
      headerTextColor: "#ffffff",
    });
  }
  return cols;
};

const buildRows = (data, type, timeline, quarters, UICols) => {
  if (!data || data.length === 0 || !type || !timeline || !quarters || !UICols) return [];

  const uniqueStages = [...new Set(data.map((r) => r[type]).filter(Boolean))];
  const acvCol = UICols.find((c) => c.type === "currency")?.col;
  const percCols = UICols.filter((c) => c.type === "perc");
  const rawCols = UICols.filter((c) => c.type !== "perc");

  // Sort by total ACV descending
  if (acvCol) {
    uniqueStages.sort((a, b) => {
      const aTotal = data.filter((r) => r[type] === a).reduce((s, r) => s + (r[acvCol] || 0), 0);
      const bTotal = data.filter((r) => r[type] === b).reduce((s, r) => s + (r[acvCol] || 0), 0);
      return bTotal - aTotal;
    });
  }

  // Quarter ACV totals for % computation
  const quarterAcvTotals = {};
  quarters.forEach((q) => {
    quarterAcvTotals[q] = data.filter((r) => r[timeline] === q).reduce((s, r) => s + (r[acvCol] || 0), 0);
  });
  const grandAcvTotal = data.reduce((s, r) => s + (r[acvCol] || 0), 0);

  const rows = uniqueStages.map((stage) => {
    const row = { [type]: stage, label: stage, rowType: "data" };
    quarters.forEach((q) => {
      const cellData = data.find((r) => r[timeline] === q && r[type] === stage);
      rawCols.forEach((c) => { row[`${c.col}_${q}`] = cellData ? cellData[c.col] ?? null : null; });
      if (acvCol) {
        const acvVal = cellData ? cellData[acvCol] || 0 : 0;
        const qTotal = quarterAcvTotals[q] || 0;
        percCols.forEach((c) => { row[`${c.col}_${q}`] = qTotal > 0 ? Math.round((acvVal / qTotal) * 100) : null; });
      }
    });
    rawCols.forEach((c) => {
      row[`${c.col}_Total`] = data.filter((r) => r[type] === stage).reduce((s, r) => s + (r[c.col] || 0), 0);
    });
    if (acvCol) {
      const stageAcvTotal = data.filter((r) => r[type] === stage).reduce((s, r) => s + (r[acvCol] || 0), 0);
      percCols.forEach((c) => { row[`${c.col}_Total`] = grandAcvTotal > 0 ? Math.round((stageAcvTotal / grandAcvTotal) * 100) : null; });
    }
    return row;
  });

  // Grand total row
  const totalRow = { [type]: "Total", label: "Total", rowType: "total", isTotal: true };
  quarters.forEach((q) => {
    rawCols.forEach((c) => { totalRow[`${c.col}_${q}`] = data.filter((r) => r[timeline] === q).reduce((s, r) => s + (r[c.col] || 0), 0); });
    percCols.forEach((c) => { totalRow[`${c.col}_${q}`] = 100; });
  });
  rawCols.forEach((c) => { totalRow[`${c.col}_Total`] = data.reduce((s, r) => s + (r[c.col] || 0), 0); });
  percCols.forEach((c) => { totalRow[`${c.col}_Total`] = 100; });
  rows.push(totalRow);

  return rows;
};

const UpsellCrossSellDetailPaper = ({
  classes,
  paperTitle,
  currencySymbol,
  upsellCrossSellData,
  Value_Label,
  handleChangeLocation,
}) => {
  const custTypeItem = upsellCrossSellData?.pipelineMixByCustType;
  const { type, data, label, UICols, timeline } = custTypeItem || {};

  const quarters = useMemo(() => {
    if (!data || !timeline) return [];
    return [...new Set(data.map((r) => r[timeline]).filter(Boolean))].sort();
  }, [data, timeline]);

  const acvLabel = `${Value_Label || 'ACV'} (${currencySymbol}K)`;

  const columns = useMemo(
    () => buildColumns(quarters, UICols, type, label, timeline, acvLabel),
    [quarters, UICols, type, label, timeline, acvLabel]
  );

  const tableRows = useMemo(
    () => buildRows(data, type, timeline, quarters, UICols),
    [data, type, timeline, quarters, UICols]
  );

  if (!upsellCrossSellData || upsellCrossSellData === "") {
    return (
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {paperTitle || "Upsell / Cross-Sell Qualified Pipeline"}
            </Typography>
          </Box>
          <Skeleton variant="rect" animation="wave" height={400} width="100%" style={{ margin: "1rem auto" }} />
        </Paper>
      </Grid>
    );
  }

  if (!custTypeItem) return null;

  const getCellValue = (row, field, rowIndex) => {
    if (field === type) return row[field] ?? "";
    const val = row[field];
    if (val == null) return "-";
    const matchedUICol = (UICols || []).find((c) => field.startsWith(c.col + "_"));
    if (matchedUICol?.type === "currency") return d3ThousandFormatter(val / 1000);
    if (matchedUICol?.type === "perc") return `${Math.round(val)}%`;
    return d3CountFormatter(val);
  };

  const getCellStyle = (row) => {
    if (row.rowType === "total") return { fontWeight: 600, backgroundColor: "#f5f5f5" };
    return {};
  };

  const handleRowClick = (row, rowIndex, columnInfo) => {
    if (!handleChangeLocation) return;
    const field = columnInfo?.field;
    if (!field || field === "serialNo" || field === type) return;
    const matchedUICol = (UICols || []).find((c) => field.startsWith(c.col + "_"));
    if (!matchedUICol || matchedUICol.type === "perc") return;
    const rowValue = row[type] ?? row.label;
    const cellValue = field.endsWith("_Total") ? "Total" : field.replace(/^[^_]+_/, "");
    handleChangeLocation("drilldownOnGraph", { row: rowValue, cell: cellValue }, type, timeline);
  };

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Typography variant="h5" className={classes.title}>
            {paperTitle || "Upsell / Cross-Sell Qualified Pipeline"}
          </Typography>
          <span className={classes.updateFrequency}>{"Live Data"}</span>
        </Box>
        {columns.length > 0 && tableRows.length > 0 && (
          <ResizableTable
            columns={columns}
            rows={tableRows}
            getCellValue={getCellValue}
            getCellStyle={getCellStyle}
            rowKey="label"
            hideSubHeaderRow={false}
            maxWidth="100%"
            onRowClick={handleChangeLocation ? handleRowClick : undefined}
          />
        )}
      </Paper>
    </Grid>
  );
};

export default UpsellCrossSellDetailPaper;
